/* Mock data — swap for your API layer when integrating.
   Palette: brand indigo primary + violet/cyan/emerald/amber/rose category hues,
   semantic green/amber/red for status. */

export const ATTENTION_ITEMS = [
  {
    id: 1,
    name: 'Meridian Trade Co.',
    sub: 'Case #C-1042 · ECDD review',
    priority: 'HIGH',
    note: 'PEP threshold exceeded — escalating senior manager approval',
    due: '3d 4h over',
    dueTone: 'over',
    sla: 92,
    action: 'Review',
    assignee: 'A. Alderson',
    opened: '12 Aug 2026',
    last: '25m ago',
    suggestion: 'Escalate to senior manager with PEP evidence pack attached.',
  },
  {
    id: 2,
    name: 'James Corrigan',
    sub: 'Case #C-0987 · Document refresh',
    priority: 'MEDIUM',
    note: 'Document schema version failed — licence expired',
    due: '1d 4h over',
    dueTone: 'over',
    sla: 74,
    action: 'Resubmit',
    assignee: 'J. Ogden',
    opened: '03 Aug 2026',
    last: '2h ago',
    suggestion: 'Request refreshed licence via automated customer nudge.',
  },
  {
    id: 3,
    name: 'Halvorsen Partners',
    sub: 'Case #C-1101 · Verification',
    priority: 'MEDIUM',
    note: 'Beneficial owners 2 of 2 unverified — chain incomplete',
    due: 'due in 8h',
    dueTone: 'soon',
    sla: 58,
    action: 'Review',
    assignee: 'A. Alderson',
    opened: '21 Aug 2026',
    last: '1h ago',
    suggestion: 'Run registry extract for both beneficial owners in parallel.',
  },
  {
    id: 4,
    name: 'Sofia Nakamura',
    sub: 'Case #C-1130 · Final approval',
    priority: 'LOW',
    note: 'Case checked — ready to approve',
    due: 'due in 8h',
    dueTone: 'soon',
    sla: 31,
    action: 'Approve',
    assignee: 'J. Ogden',
    opened: '24 Aug 2026',
    last: '4h ago',
    suggestion: 'All checks passed — issue certificate on approval.',
  },
];

export const KPIS = [
  {
    label: 'Pending Applications',
    value: 41,
    delta: '+12%',
    deltaTone: 'up',
    caption: '21 individual · 12 joint · 8 sole trader',
    icon: 'briefcase',
    tone: 'blue',
    spark: '#375AE7',
    sparkData: [12, 18, 14, 22, 19, 26, 24, 31],
  },
  {
    label: 'Certificates issued · 30d',
    value: 128,
    delta: '+24%',
    deltaTone: 'up',
    caption: 'Average issue time 2.1 days',
    icon: 'badge',
    tone: 'violet',
    spark: '#6D28D9',
    sparkData: [40, 52, 48, 61, 70, 66, 84, 96],
  },
  {
    label: 'Verifications',
    value: 347,
    delta: '+20%',
    deltaTone: 'up',
    caption: '41 in progress · 8 overdue',
    icon: 'shield',
    tone: 'green',
    spark: '#16A34A',
    sparkData: [120, 160, 150, 210, 240, 260, 300, 347],
  },
  {
    label: 'Total Customers',
    value: 3,
    delta: '+20%',
    deltaTone: 'up',
    caption: 'Last 30d: 2 new, 1 removed',
    icon: 'manage_accounts',
    tone: 'cyan', // was 'green'
    spark: '#0E7490', // amber-600 · was '#16A34A'
    sparkData: [1, 1, 2, 2, 3, 2, 3, 3], // real 30d trend instead of the copied verification data
  },
];

/* 12-week throughput for the interactive trend chart */
export const THROUGHPUT = [
  { week: 'W1', certificates: 18, verifications: 42 },
  { week: 'W2', certificates: 24, verifications: 51 },
  { week: 'W3', certificates: 21, verifications: 47 },
  { week: 'W4', certificates: 30, verifications: 63 },
  { week: 'W5', certificates: 27, verifications: 58 },
  { week: 'W6', certificates: 35, verifications: 71 },
  { week: 'W7', certificates: 33, verifications: 69 },
  { week: 'W8', certificates: 41, verifications: 82 },
  { week: 'W9', certificates: 38, verifications: 77 },
  { week: 'W10', certificates: 46, verifications: 90 },
  { week: 'W11', certificates: 44, verifications: 86 },
  { week: 'W12', certificates: 52, verifications: 98 },
];

/* Soft premium progression ramp — muted pastels, bottleneck flagged amber.
   Each stage carries defined operational detail for the explorer panel. */
export const FUNNEL = [
  {
    label: 'Draft',
    value: 7,
    sub: 'idle 2.1d',
    avg: '2.1d in stage',
    oldest: '6d waiting',
    desc: 'Cases created but not yet sent to the customer for input.',
    colors: ['#CBD5E1', '#E2E8F0'],
  },
  {
    label: 'Awaiting customer',
    value: 19,
    sub: '',
    subTone: 'risk',
    avg: '9.4d in stage',
    oldest: '14d waiting',
    slaRisk: 7,
    desc: 'Waiting on customer documents or responses.',
    colors: ['#F59E0B', '#FCD34D'],
    bottleneck: true,
  },
  {
    label: 'Verification',
    value: 8,
    sub: '1.8d avg',
    avg: '1.8d in stage',
    oldest: '5d waiting',
    desc: 'Registry, identity and document verification checks in progress.',
    colors: ['#60A5FA', '#93C5FD'],
  },
  {
    label: 'Review',
    value: 6,
    sub: '0.9d avg',
    avg: '0.9d in stage',
    oldest: '2d waiting',
    desc: 'Analyst review of completed files before final sign-off.',
    colors: ['#818CF8', '#A5B4FC'],
  },
  {
    label: 'Approvals',
    value: 4,
    sub: '0.4d avg',
    avg: '0.4d in stage',
    oldest: '1d waiting',
    desc: 'Final approval queue — ready to issue on sign-off.',
    colors: ['#A78BFA', '#C4B5FD'],
  },
  {
    label: 'Approved',
    value: 128,
    sub: 'last 30d',
    avg: '2.1d to issue',
    oldest: '—',
    desc: 'Certificates issued within the period. Average issue time 2.1 days.',
    colors: ['#10B981', '#34D399'],
  },
];

export const RISK_MIX = [
  { label: 'Low', value: 164, pct: 62, color: '#10B981' },
  { label: 'Medium', value: 89, pct: 27, color: '#F59E0B' },
  { label: 'High', value: 28, pct: 10, color: '#EF4444' },
  { label: 'Unacceptable', value: 3, pct: 1, color: '#334155' },
];

export const ACTIVITY = [
  {
    tone: '#10B981',
    text: 'Certificate RTP-2025-0941 issued to Sofia Nakamura — approved by J. Ogden',
    time: '2m ago',
  },
  {
    tone: '#375AE7',
    text: 'Kaseid Ledger verified via share (OTT-2025-0755)',
    time: '1h ago',
  },
  {
    tone: '#F59E0B',
    text: 'Risk rating raised: Medium → High on Halvorsen Partners — reason recorded by A. Alderson',
    time: '2h ago',
  },
  {
    tone: '#94A3B8',
    text: 'Mobile file opened by James Corrigan via mobile',
    time: '1d ago',
  },
  {
    tone: '#94A3B8',
    text: 'Rule run completed — geography weighting updated',
    time: '4d ago',
  },
];
